import { isQuantityString, parseQuantity, parseRialString, quantityCostRial } from '@clinic/money-date';
import { z } from 'zod';
import {
  AMOUNT_MAX_RIAL,
  AMOUNT_TOO_BIG,
  AmountRialSchema,
  DateOnlySchema,
  IsoDateTimeSchema,
  PAGE_LIMIT_MAX,
  RialStringSchema,
  UuidSchema,
  optionalText,
} from './common.js';
import { isValidGtin } from './barcode.js';
import { CatalogProductSchema, ClinicCatalogSubmissionSchema } from './catalog.js';
import { CreditInputSchema } from './finance.js';

/**
 * Clinic inventory (docs/clinic-inventory/design.md, ADR-007, ADR-030). Quantities are decimal
 * strings with up to three decimals ("2.5"), never JSON numbers: the server counts in thousandths.
 */

// ---- quantities ----

export const QuantitySchema = z.string().refine(isQuantityString, { message: 'مقدار باید عدد با حداکثر سه رقم اعشار باشد.' });

// Each refinement below runs even when the format check failed (Zod keeps checking), so it must
// not parse a malformed string: that throws, and a thrown error is a 500 instead of a message.

/** Both strings are well-formed quantities (a cross-field check compares only those). */
const bothQuantities = (a: string, b: string) => isQuantityString(a) && isQuantityString(b);

/** A quantity greater than zero. */
export const PositiveQuantitySchema = QuantitySchema.refine((v) => !isQuantityString(v) || parseQuantity(v) > 0n, {
  message: 'مقدار باید بیشتر از صفر باشد.',
});

/** Zero or more (thresholds). */
export const NonNegativeQuantitySchema = QuantitySchema.refine((v) => !isQuantityString(v) || parseQuantity(v) >= 0n, {
  message: 'مقدار منفی مجاز نیست.',
});

/** A signed change that is not zero (manual adjustments). */
export const QuantityDeltaSchema = QuantitySchema.refine((v) => !isQuantityString(v) || parseQuantity(v) !== 0n, {
  message: 'مقدار تغییر نمی‌تواند صفر باشد.',
});

// ---- identifiers on labels (ADR-024) ----

/** GTIN-8/12/13/14 with a valid check digit, stored as 14 digits. */
export const GtinSchema = z
  .string()
  .trim()
  .refine(isValidGtin, { message: 'بارکد کالا (GTIN) معتبر نیست.' })
  .transform((v) => v.padStart(14, '0'));

/** The 20-digit tracking id of an authenticity label, unique per unit (BR-30). */
export const StockUidSchema = z.string().regex(/^\d{20}$/, { message: 'شناسه‌ی رهگیری باید ۲۰ رقم باشد.' });

// ---- expiry (BR-10) ----

export const EXPIRY_STATES = ['expired', 'alert', 'warn', 'ok', 'none'] as const;
export type ExpiryState = (typeof EXPIRY_STATES)[number];


export const DEFAULT_EXPIRY_ALERT_DAYS = 30;
export const DEFAULT_EXPIRY_WARN_DAYS = 90;

/** Whole days from `today` to `expiryDate` (both `YYYY-MM-DD`); negative once expired. */
export function daysUntil(expiryDate: string, today: string): number {
  return Math.round((Date.parse(`${expiryDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}

/**
 * The legacy `expInfo` rule with the clinic's thresholds: past the date is expired, within
 * `alertDays` is red, within `warnDays` amber. A product is usable through its expiry date.
 */
export function expiryStateOf(
  expiryDate: string | null,
  today: string,
  thresholds: { alertDays: number; warnDays: number },
): ExpiryState {
  if (!expiryDate) return 'none';
  const days = daysUntil(expiryDate, today);
  if (days < 0) return 'expired';
  if (days <= thresholds.alertDays) return 'alert';
  if (days <= thresholds.warnDays) return 'warn';
  return 'ok';
}

// ---- items ----

export const ITEM_STATUSES = ['active', 'archived'] as const;

/** Suggestions from the legacy app (docs/legacy/reference-data.md §3); any short text is allowed. */
export const SUGGESTED_ITEM_CATEGORIES = ['فیلر', 'بوتاکس', 'مزوژل', 'نخ', 'مصرفی'] as const;
export const SUGGESTED_ITEM_UNITS = ['ویال', 'سرنگ', 'عدد', 'تیوب', 'بسته', 'میلی‌لیتر', 'آمپول', 'سی‌سی'] as const;

export const InventoryItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /** The English name (ADR-053); the search reads both. */
  nameEn: z.string().nullable(),
  category: z.string().nullable(),
  unit: z.string(),
  lowThreshold: QuantitySchema,
  gtin: z.string().nullable(),
  status: z.enum(ITEM_STATUSES),
  /** Sum over all batches, expired ones included (what is physically there). */
  onHand: QuantitySchema,
  /** What can still be used: batches that are not expired. Low stock is measured on this (BR-09). */
  usableOnHand: QuantitySchema,
  isLow: z.boolean(),
  nearestExpiry: DateOnlySchema.nullable(),
  expiryState: z.enum(EXPIRY_STATES),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type InventoryItem = z.infer<typeof InventoryItemSchema>;

export const InventoryItemInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'نام کالا را بنویسید.' })
    .max(120, { message: 'نام کالا حداکثر ۱۲۰ نویسه است.' }),
  nameEn: optionalText(120),
  category: optionalText(40),
  unit: z
    .string()
    .trim()
    .min(1, { message: 'واحد را بنویسید (مثلاً ویال).' })
    .max(20, { message: 'واحد حداکثر ۲۰ نویسه است.' }),
  lowThreshold: NonNegativeQuantitySchema.default('2'),
  gtin: GtinSchema.nullish().transform((v) => v ?? null),
});
export type InventoryItemInput = z.input<typeof InventoryItemInputSchema>;

export const InventoryItemListQuerySchema = z.object({
  q: z.string().trim().max(60).optional(),
  status: z.enum(ITEM_STATUSES).default('active'),
  /** Exact GTIN (14 digits after normalization): the item a scanned label belongs to. */
  gtin: GtinSchema.optional(),
  /** Opaque keyset cursor (name order). */
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
});
export type InventoryItemListQuery = z.infer<typeof InventoryItemListQuerySchema>;
export const InventoryItemListResponseSchema = z.object({
  items: z.array(InventoryItemSchema),
  nextCursor: z.string().nullable(),
});
export type InventoryItemListResponse = z.infer<typeof InventoryItemListResponseSchema>;

// ---- batches ----

export const StockBatchSchema = z.object({
  id: UuidSchema,
  itemId: UuidSchema,
  lotNo: z.string().nullable(),
  expiryDate: DateOnlySchema.nullable(),
  expiryState: z.enum(EXPIRY_STATES),
  qtyOnHand: QuantitySchema,
  /** Set on an opened vial: what was left after a use (BR-06). */
  openedAt: IsoDateTimeSchema.nullable(),
  parentBatchId: UuidSchema.nullable(),
  /** Kept for this patient only (BR-07). */
  reservedPatientId: UuidSchema.nullable(),
  reservedPatientName: z.string().nullable(),
  unitCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type StockBatch = z.infer<typeof StockBatchSchema>;

export const InventoryItemDetailSchema = z.object({
  item: InventoryItemSchema,
  /** In FEFO order (BR-05). Empty batches are included; the web hides them when others exist. */
  batches: z.array(StockBatchSchema),
  /** The approved catalog product of the item's GTIN (ADR-055); the clinic's own name stays. */
  catalog: CatalogProductSchema.nullable(),
  /** This clinic's latest proposal of the item's GTIN, while the catalog does not have it. */
  catalogSubmission: ClinicCatalogSubmissionSchema.nullable(),
});
export type InventoryItemDetail = z.infer<typeof InventoryItemDetailSchema>;

// ---- movements ----

export const MOVEMENT_TYPES = [
  'receipt',
  'consumption',
  'open_split',
  'return_to_supplier',
  'damage',
  'expiry_writeoff',
  'adjustment',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

export const StockMovementSchema = z.object({
  id: UuidSchema,
  batchId: UuidSchema,
  itemId: UuidSchema,
  type: z.enum(MOVEMENT_TYPES),
  /** Signed: positive adds to the batch, negative takes from it. */
  qty: QuantitySchema,
  lotNo: z.string().nullable(),
  expiryDate: DateOnlySchema.nullable(),
  unitCostRial: RialStringSchema.nullable(),
  receiptId: UuidSchema.nullable(),
  visitId: UuidSchema.nullable(),
  reason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type StockMovement = z.infer<typeof StockMovementSchema>;
export const StockMovementListResponseSchema = z.object({
  items: z.array(StockMovementSchema),
  nextCursor: UuidSchema.nullable(),
});

// ---- receipts (BR-08, BR-26, BR-30) ----

/** A purchase, or the stock a clinic already had when it started (ADR-056): never a purchase, no price. */
export const RECEIPT_KINDS = ['purchase', 'opening'] as const;
export type ReceiptKind = (typeof RECEIPT_KINDS)[number];

export const ReceiptLineInputSchema = z.object({
  itemId: UuidSchema,
  qty: PositiveQuantitySchema,
  lotNo: optionalText(60),
  expiryDate: DateOnlySchema.nullish().transform((v) => v ?? null),
  unitCostRial: AmountRialSchema.nullish().transform((v) => v ?? null),
  /** Tracking ids scanned from the authenticity labels of this line (at most one per unit). */
  uids: z
    .array(StockUidSchema)
    .max(500)
    .refine((v) => new Set(v).size === v.length, { message: 'یک برچسب دو بار اسکن شده است.' })
    .default([]),
});

export const ReceiptInputSchema = z.object({
  kind: z.enum(RECEIPT_KINDS).default('purchase'),
  supplierName: optionalText(120),
  reference: optionalText(60),
  note: optionalText(500),
  /** Bought on credit (ADR-038): only the clinic admin; the purchase is owed to this vendor. */
  credit: CreditInputSchema.nullish().transform((v) => v ?? null),
  lines: z
    .array(ReceiptLineInputSchema)
    .min(1, { message: 'دست‌کم یک قلم ثبت کنید.' })
    .max(50)
    .refine((lines) => lines.every((l) => l.uids.length === 0 || !isQuantityString(l.qty) || BigInt(l.uids.length) * 1000n <= parseQuantity(l.qty)), {
      message: 'تعداد برچسب‌های اسکن‌شده از مقدار قلم بیشتر است.',
    })
    .refine((lines) => new Set(lines.flatMap((l) => l.uids)).size === lines.flatMap((l) => l.uids).length, {
      message: 'یک برچسب در دو قلم آمده است.',
    })
    // The receipt is booked as one material purchase: its total stays within the books' ceiling.
    .refine(
      (lines) => {
        try {
          const total = lines.reduce(
            (sum, l) => (l.unitCostRial === null ? sum : sum + quantityCostRial(parseQuantity(l.qty), parseRialString(l.unitCostRial))),
            0n,
          );
          return total <= AMOUNT_MAX_RIAL;
        } catch {
          return true; // a malformed line already has its own message
        }
      },
      { message: AMOUNT_TOO_BIG },
    ),
}).refine((r) => r.kind !== 'opening' || (r.credit === null && r.lines.every((l) => l.unitCostRial === null)), {
  message: 'موجودی اولیه خرید نیست و قیمت خرید یا نسیه نمی‌گیرد.',
  path: ['kind'],
});
export type ReceiptInput = z.input<typeof ReceiptInputSchema>;

export const ReceiptSchema = z.object({
  id: UuidSchema,
  kind: z.enum(RECEIPT_KINDS),
  supplierName: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  lines: z.array(
    z.object({
      movementId: UuidSchema,
      itemId: UuidSchema,
      itemName: z.string(),
      unit: z.string(),
      batchId: UuidSchema,
      qty: QuantitySchema,
      lotNo: z.string().nullable(),
      expiryDate: DateOnlySchema.nullable(),
      /** Given at receipt or later (ADR-053); null without `inventory.cost` or while pending. */
      unitCostRial: RialStringSchema.nullable(),
      /** Received without a purchase price, none given yet. */
      costPending: z.boolean(),
      uids: z.array(z.string()),
    }),
  ),
});
export type Receipt = z.infer<typeof ReceiptSchema>;

// ---- purchase price given later (ADR-053) ----

/** A receipt line received without a purchase price. */
export const PendingCostSchema = z.object({
  movementId: UuidSchema,
  receiptId: UuidSchema,
  receivedAt: IsoDateTimeSchema,
  receivedBy: z.string().nullable(),
  supplierName: z.string().nullable(),
  itemId: UuidSchema,
  itemName: z.string(),
  itemNameEn: z.string().nullable(),
  unit: z.string(),
  qty: QuantitySchema,
  lotNo: z.string().nullable(),
  expiryDate: DateOnlySchema.nullable(),
});
export type PendingCost = z.infer<typeof PendingCostSchema>;
export const PendingCostListResponseSchema = z.object({
  items: z.array(PendingCostSchema),
  /** All pending lines (the list shows at most 200). */
  total: z.number().int(),
});
export type PendingCostListResponse = z.infer<typeof PendingCostListResponseSchema>;

/** Prices for lines of one receipt; each line once. Zero is «free»: nothing goes to the books. */
export const ReceiptCostsInputSchema = z.object({
  lines: z
    .array(z.object({ movementId: UuidSchema, unitCostRial: AmountRialSchema }))
    .min(1, { message: 'دست‌کم یک قیمت بنویسید.' })
    .max(50)
    .refine((lines) => new Set(lines.map((l) => l.movementId)).size === lines.length, { message: 'یک قلم دو بار آمده است.' }),
});
export type ReceiptCostsInput = z.input<typeof ReceiptCostsInputSchema>;
export const ReceiptListResponseSchema = z.object({
  items: z.array(ReceiptSchema),
  nextCursor: UuidSchema.nullable(),
});

// ---- manual corrections (admin) ----

export const StockAdjustInputSchema = z.object({
  qtyDelta: QuantityDeltaSchema,
  reason: z.string().trim().min(3, { message: 'دلیل اصلاح را بنویسید.' }).max(300),
});
export type StockAdjustInput = z.input<typeof StockAdjustInputSchema>;

export const WRITE_OFF_TYPES = ['damage', 'expiry_writeoff', 'return_to_supplier'] as const;
export type WriteOffType = (typeof WRITE_OFF_TYPES)[number];
export const StockWriteOffInputSchema = z.object({
  type: z.enum(WRITE_OFF_TYPES),
  qty: PositiveQuantitySchema,
  reason: z.string().trim().min(3, { message: 'دلیل را بنویسید.' }).max(300),
});
export type StockWriteOffInput = z.input<typeof StockWriteOffInputSchema>;

// ---- alerts (BR-09, BR-10) ----

export const InventoryAlertsSchema = z.object({
  lowStock: z.array(
    z.object({ itemId: UuidSchema, name: z.string(), unit: z.string(), usableOnHand: QuantitySchema, lowThreshold: QuantitySchema }),
  ),
  expiring: z.array(
    z.object({
      batchId: UuidSchema,
      itemId: UuidSchema,
      itemName: z.string(),
      unit: z.string(),
      lotNo: z.string().nullable(),
      expiryDate: DateOnlySchema,
      qtyOnHand: QuantitySchema,
      state: z.enum(['expired', 'alert', 'warn']),
      daysLeft: z.number().int(),
    }),
  ),
});
export type InventoryAlerts = z.infer<typeof InventoryAlertsSchema>;

// ---- trace (BR-31) ----

export const TraceQuerySchema = z
  .object({ itemId: UuidSchema.optional(), lot: z.string().trim().min(1).max(60).optional(), uid: StockUidSchema.optional() })
  .refine((q) => (q.uid !== undefined) !== (q.itemId !== undefined && q.lot !== undefined), {
    message: 'یا شناسه‌ی رهگیری، یا کالا و Lot را بدهید.',
  });
export const TraceResponseSchema = z.object({
  items: z.array(
    z.object({
      patientId: UuidSchema,
      patientName: z.string(),
      patientPhone: z.string().nullable(),
      visitId: UuidSchema,
      consumedAt: IsoDateTimeSchema,
      itemName: z.string(),
      lotNo: z.string().nullable(),
      qty: QuantitySchema,
      uid: z.string().nullable(),
    }),
  ),
});
export type TraceResponse = z.infer<typeof TraceResponseSchema>;

// ---- settings (BR-10) ----

export const InventorySettingsSchema = z.object({ expiryAlertDays: z.number().int(), expiryWarnDays: z.number().int() });
export type InventorySettings = z.infer<typeof InventorySettingsSchema>;
export const InventorySettingsInputSchema = z
  .object({
    expiryAlertDays: z.number().int().min(1).max(365),
    expiryWarnDays: z.number().int().min(1).max(730),
  })
  .refine((v) => v.expiryWarnDays >= v.expiryAlertDays, {
    message: 'بازه‌ی هشدار زرد باید دست‌کم برابر بازه‌ی قرمز باشد.',
    path: ['expiryWarnDays'],
  });

// ---- FEFO and the materials of a visit (BR-05..07) ----

export const FefoQuerySchema = z.object({ itemId: UuidSchema, patientId: UuidSchema });
export const FefoResponseSchema = z.object({
  /** Usable batches, best first: opened for this patient, opened, then nearest expiry. */
  batches: z.array(StockBatchSchema.extend({ reservedForThisPatient: z.boolean() })),
});
export type FefoResponse = z.infer<typeof FefoResponseSchema>;

export const VisitMaterialSchema = z.object({
  id: UuidSchema,
  visitId: UuidSchema,
  visitItemId: UuidSchema.nullable(),
  batchId: UuidSchema,
  itemId: UuidSchema,
  itemName: z.string(),
  unit: z.string(),
  lotNo: z.string().nullable(),
  expiryDate: DateOnlySchema.nullable(),
  batchOpened: z.boolean(),
  /** Taken from the batch ("برداشت"). */
  takeQty: QuantitySchema,
  /** Actually used on the patient ("مصرف واقعی"), at most `takeQty`. */
  usedQty: QuantitySchema,
  /** Keep what is left of an opened vial for this patient only (BR-07). */
  keepForPatient: z.boolean(),
  uid: z.string().nullable(),
  /** Set when the visit completed and the stock moved; the line is frozen from then on. */
  appliedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type VisitMaterial = z.infer<typeof VisitMaterialSchema>;
export const VisitMaterialListResponseSchema = z.object({ items: z.array(VisitMaterialSchema) });
export type VisitMaterialListResponse = z.infer<typeof VisitMaterialListResponseSchema>;

export const VisitMaterialInputSchema = z
  .object({
    batchId: UuidSchema,
    visitItemId: UuidSchema.nullish().transform((v) => v ?? null),
    takeQty: PositiveQuantitySchema,
    /** Defaults to everything taken. */
    usedQty: NonNegativeQuantitySchema.optional(),
    keepForPatient: z.boolean().default(false),
    /** The authenticity label of the unit used, when it was scanned. */
    uid: StockUidSchema.nullish().transform((v) => v ?? null),
  })
  .refine((v) => v.usedQty === undefined || !bothQuantities(v.usedQty, v.takeQty) || parseQuantity(v.usedQty) <= parseQuantity(v.takeQty), {
    message: 'مصرف واقعی نمی‌تواند از برداشت بیشتر باشد.',
    path: ['usedQty'],
  });
export type VisitMaterialInput = z.input<typeof VisitMaterialInputSchema>;

export const VisitMaterialUpdateInputSchema = z
  .object({
    takeQty: PositiveQuantitySchema,
    usedQty: NonNegativeQuantitySchema,
    keepForPatient: z.boolean(),
  })
  .refine((v) => !bothQuantities(v.usedQty, v.takeQty) || parseQuantity(v.usedQty) <= parseQuantity(v.takeQty), {
    message: 'مصرف واقعی نمی‌تواند از برداشت بیشتر باشد.',
    path: ['usedQty'],
  });
export type VisitMaterialUpdateInput = z.input<typeof VisitMaterialUpdateInputSchema>;

/** What completing the visit will do to the stock, one line per material (ADR-016 principle 4). */
export const MATERIAL_PROBLEMS = ['out_of_stock', 'expired', 'reserved', 'batch_gone'] as const;
export const InventoryEffectSchema = z.object({
  lines: z.array(
    z.object({
      materialId: UuidSchema,
      itemName: z.string(),
      unit: z.string(),
      lotNo: z.string().nullable(),
      used: QuantitySchema,
      /** Left in a newly opened vial (0 when nothing is left or the batch was already open). */
      leftover: QuantitySchema,
      keepForPatient: z.boolean(),
      problem: z.enum(MATERIAL_PROBLEMS).nullable(),
    }),
  ),
});
export type InventoryEffect = z.infer<typeof InventoryEffectSchema>;
